import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { toPng } from "html-to-image";
import api from "@/lib/api";
import { formatEventDate, formatPrice } from "@/lib/format";
import { CheckCircle2, Calendar, MapPin, ArrowLeft, Share2, Download, Sparkles, Ticket as TicketIcon, Armchair } from "lucide-react";
import PerforatedDivider from "@/components/PerforatedDivider";
import { toast } from "sonner";

export default function TicketPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [sharing, setSharing] = useState(false);
  const shareRef = useRef(null);

  useEffect(() => {
    api.get(`/tickets/${id}${token ? `?token=${encodeURIComponent(token)}` : ""}`)
      .then((r) => setTicket(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Billet introuvable"))
      .finally(() => setLoading(false));
  }, [id, token]);

  const generateImage = async () => {
    if (!shareRef.current) return null;
    return toPng(shareRef.current, { cacheBust: true, pixelRatio: 2, backgroundColor: "#01696F", skipFonts: true });
  };

  const shareTicket = async () => {
    setSharing(true);
    try {
      const dataUrl = await generateImage();
      if (!dataUrl) throw new Error("Génération impossible");
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], `billet-${ticket.id}.png`, { type: "image/png" });
      const shareText = `J'ai mon billet ${ticket.seat_label ? `(siège ${ticket.seat_label}) ` : ""}pour ${ticket.event?.title} ! 🎉`;
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: ticket.event?.title, text: shareText });
        toast.success("Partagé !");
      } else if (navigator.share) {
        await navigator.share({ title: ticket.event?.title, text: shareText, url: window.location.href });
      } else {
        // Fallback: download
        const a = document.createElement("a");
        a.href = dataUrl;
        a.download = `billet-${ticket.id}.png`;
        a.click();
        toast.success("Image téléchargée");
      }
    } catch (e) {
      if (e?.name !== "AbortError") {
        toast.error("Partage impossible : " + (e?.message || "réessayez"));
      }
    } finally {
      setSharing(false);
    }
  };

  const downloadImage = async () => {
    setSharing(true);
    try {
      const dataUrl = await generateImage();
      if (!dataUrl) throw new Error("Génération impossible");
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `billet-${ticket.id}.png`;
      a.click();
      toast.success("Image téléchargée");
    } catch (e) {
      toast.error("Téléchargement impossible");
    } finally {
      setSharing(false);
    }
  };

  if (loading) return <div className="z-container" style={{ textAlign: "center" }}><span className="z-loader z-loader-dark" /></div>;
  if (err) return <div className="z-container" data-testid="ticket-error">{err}</div>;

  const e = ticket.event;
  const isScanned = ticket.status === "scanned";

  return (
    <div className="z-container" style={{ maxWidth: 480 }} data-testid="ticket-page">
      <div style={{ background: "rgba(67,122,34,0.10)", color: "var(--z-success)", padding: 14, borderRadius: 12, marginBottom: 18, display: "flex", gap: 10, alignItems: "center" }}>
        <CheckCircle2 size={20} />
        <div style={{ fontSize: 14, fontWeight: 600 }}>Votre billet est confirmé !</div>
      </div>

      {/* Visible ticket card */}
      <div style={{ background: "white", borderRadius: 20, padding: 22, border: "1px solid var(--z-border)", boxShadow: "0 4px 18px rgba(0,0,0,0.06)" }} data-testid="ticket-card">
        <h2 style={{ fontSize: 20, fontWeight: 800, margin: "0 0 12px", color: "var(--z-teal)" }} data-testid="ticket-event-title">
          {e?.title}
        </h2>
        <div style={{ display: "flex", gap: 8, alignItems: "center", color: "var(--z-text)", fontSize: 14, marginBottom: 6 }}>
          <Calendar size={15} /> {formatEventDate(e?.starts_at)}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", color: "var(--z-text)", fontSize: 14, marginBottom: 14 }}>
          <MapPin size={15} /> {e?.location || "Lieu communiqué"}
        </div>

        {(ticket.category_name || ticket.seat_label) && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
            {ticket.category_name && (
              <span className="z-chip z-chip-active" style={{ fontSize: 13 }} data-testid="ticket-category">
                <TicketIcon size={13} /> {ticket.category_name}
              </span>
            )}
            {ticket.seat_label && (
              <span className="z-chip" style={{ fontSize: 13, background: "var(--z-orange)", color: "white" }} data-testid="ticket-seat">
                <Armchair size={13} /> Siège {ticket.seat_label}
              </span>
            )}
          </div>
        )}

        <PerforatedDivider />

        <div className="z-qr-wrap" style={{ marginTop: 16 }}>
          <QRCodeSVG value={ticket.qr_payload} size={220} level="H" data-testid="ticket-qr" />
        </div>

        <div style={{ textAlign: "center", marginTop: 16 }}>
          {isScanned ? (
            <span className="z-badge" style={{ background: "rgba(161,44,123,0.12)", color: "var(--z-error)" }} data-testid="ticket-status">
              Déjà scanné
            </span>
          ) : (
            <span className="z-badge z-badge-success" data-testid="ticket-status">Valide</span>
          )}
        </div>

        <div style={{ marginTop: 14, textAlign: "center", color: "var(--z-muted)", fontSize: 12 }}>
          Réf. : {ticket.id}
        </div>
      </div>

      {/* Share actions */}
      <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
        <button onClick={shareTicket} disabled={sharing} className="z-btn z-btn-primary" style={{ flex: 1 }} data-testid="ticket-share-btn">
          <Share2 size={16} /> {sharing ? "..." : "Partager"}
        </button>
        <button onClick={downloadImage} disabled={sharing} className="z-btn z-btn-outline" data-testid="ticket-download-btn">
          <Download size={16} />
        </button>
      </div>

      <div style={{ textAlign: "center", marginTop: 18 }}>
        <Link to="/events" className="z-link" style={{ display: "inline-flex", alignItems: "center", gap: 6 }} data-testid="ticket-back-link">
          <ArrowLeft size={16} /> Retour aux événements
        </Link>
      </div>

      {/* Hidden shareable card — visually rich, used only for image export */}
      <div style={{ position: "fixed", left: "-9999px", top: 0, pointerEvents: "none" }} aria-hidden>
        <div
          ref={shareRef}
          style={{
            width: 600,
            background: "linear-gradient(135deg, #01696F 0%, #023d40 70%, #001a1b 100%)",
            padding: 36,
            fontFamily: "'Work Sans', sans-serif",
            color: "white",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Decorative orange accent */}
          <div style={{ position: "absolute", top: -60, right: -60, width: 200, height: 200, background: "#DA7101", borderRadius: "50%", opacity: 0.18 }} />
          <div style={{ position: "absolute", bottom: -80, left: -80, width: 240, height: 240, background: "#DA7101", borderRadius: "50%", opacity: 0.12 }} />

          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 22, position: "relative" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Sparkles size={22} color="#DA7101" />
              <div>
                <div style={{ fontWeight: 800, fontSize: 18, lineHeight: 1, letterSpacing: -0.3 }}>
                  <span style={{ color: "#DA7101" }}>z</span>events
                </div>
                <div style={{ fontSize: 9, opacity: 0.7, fontWeight: 600, letterSpacing: 1.2, marginTop: 2 }}>
                  by ALO FINANCES
                </div>
              </div>
            </div>
          </div>

          {/* Title */}
          <div style={{ position: "relative", marginBottom: 22 }}>
            <div style={{ fontSize: 12, letterSpacing: 2, textTransform: "uppercase", opacity: 0.7, marginBottom: 6 }}>J&apos;y serai !</div>
            <h1 style={{ margin: 0, fontSize: 32, fontWeight: 800, lineHeight: 1.1, color: "white", wordBreak: "break-word" }}>{e?.title || "Événement"}</h1>
          </div>

          {/* Info grid */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 22, position: "relative" }}>
            <div style={{ background: "rgba(255,255,255,0.08)", padding: 12, borderRadius: 10 }}>
              <div style={{ fontSize: 10, opacity: 0.6, textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 4 }}>Date</div>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{formatEventDate(e?.starts_at)}</div>
            </div>
            <div style={{ background: "rgba(255,255,255,0.08)", padding: 12, borderRadius: 10 }}>
              <div style={{ fontSize: 10, opacity: 0.6, textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 4 }}>Lieu</div>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{e?.location || "—"}</div>
            </div>
          </div>

          {/* Category + seat highlight */}
          {(ticket.category_name || ticket.seat_label) && (
            <div style={{ display: "flex", gap: 10, marginBottom: 24, position: "relative", flexWrap: "wrap" }}>
              {ticket.category_name && (
                <div style={{ background: "#DA7101", padding: "8px 16px", borderRadius: 8, fontWeight: 700, fontSize: 14 }}>
                  {ticket.category_name}
                </div>
              )}
              {ticket.seat_label && (
                <div style={{ background: "white", color: "#01696F", padding: "8px 16px", borderRadius: 8, fontWeight: 800, fontSize: 16 }}>
                  Siège {ticket.seat_label}
                </div>
              )}
            </div>
          )}

          {/* QR section */}
          <div style={{ background: "white", padding: 18, borderRadius: 14, display: "flex", gap: 18, alignItems: "center", position: "relative" }}>
            <div style={{ background: "white", padding: 4 }}>
              <QRCodeSVG value={ticket.qr_payload} size={130} level="H" />
            </div>
            <div style={{ flex: 1, color: "#222" }}>
              <div style={{ fontSize: 10, color: "#666", textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 4 }}>Code d&apos;entrée</div>
              <div style={{ fontFamily: "monospace", fontSize: 11, color: "#222", wordBreak: "break-all", lineHeight: 1.4 }}>
                {ticket.id}
              </div>
              {(e?.price > 0 && ticket.price_paid > 0) && (
                <div style={{ marginTop: 8, fontSize: 13, fontWeight: 700, color: "#01696F" }}>
                  {formatPrice(ticket.price_paid, e?.currency)}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div style={{ marginTop: 22, textAlign: "center", fontSize: 12, opacity: 0.65, position: "relative" }}>
            Réservé sur <strong>zevents.digital</strong> · by ALO FINANCES · Présentez ce QR à l&apos;entrée
          </div>
        </div>
      </div>
    </div>
  );
}
